/** @format */

import StraightIcon from '@mui/icons-material/Straight';

import {flexRender, Header, HeaderGroup, Table} from '@tanstack/react-table';

import {useTheme} from '@mui/material/styles';

import {
  ARROW_CELL_WIDTH,
  CELL_HEIGHT,
  CHECKBOX_CELL_WIDTH,
  CHECKBOX_OFFSET_X,
  CHECKBOX_OFFSET_Y,
  HEAD_HEIGHT,
} from '../../constants';
import {ConstantsList, TanStackTableData, TableVariant} from '../../types';
import {StyledTableHeadRow, StyledTableSortLabel} from './styles';
import {TableCellMemoized} from '../TableCellMemoized';
import {ColumnResizer} from '../ColumnResizer';
import {StyledCheckbox} from '../../styles';

interface Props {
  headerGroup: HeaderGroup<TanStackTableData>;
  isWithSubRows: boolean;
  allSubColumnIds: string[];
  table: Table<TanStackTableData>;
  enableRowSelection?: boolean;
  isAllSelectionDisabled?: boolean;
  index: number;
  constants: ConstantsList;
  variant: TableVariant;
  // Задан — у листовых колонок появляется ручка ресайза. Один проп вместо пары колбэков:
  // невозможно передать половину конфигурации.
  columnResizing?: ColumnResizingHandlers;
}

export interface ColumnResizingHandlers {
  handleResize: (columnId: string, width: number) => void;
  handleReset: (columnId: string) => void;
}

const TableHeadRow = ({
  isWithSubRows,
  allSubColumnIds,
  headerGroup,
  table,
  enableRowSelection,
  isAllSelectionDisabled,
  index,
  constants,
  variant,
  columnResizing,
}: Props) => {
  const theme = useTheme();
  const isWithGroups = allSubColumnIds.length > 0;

  const {
    cellHeight = CELL_HEIGHT,
    headHeight = HEAD_HEIGHT,
    checkboxOffsetX = CHECKBOX_OFFSET_X,
    checkboxOffsetY = CHECKBOX_OFFSET_Y,
    arrowCellWidth = ARROW_CELL_WIDTH,
    checkboxCellWidth = CHECKBOX_CELL_WIDTH,
  } = constants;

  const mapHeader = (header: Header<TanStackTableData, unknown>) => {
    const {column, colSpan, id, subHeaders} = header;
    const hasSubHeaders = subHeaders.length > 0;
    const isRemove = isWithGroups && !allSubColumnIds.includes(id) && !hasSubHeaders;
    if (isRemove) {
      return null;
    }
    const isCollapse = hasSubHeaders && !column.columns.length;
    const canSort = column.getCanSort();
    const isSorted = Boolean(column.getIsSorted());
    const orderBy = column.getNextSortingOrder() || undefined;

    return (
      <TableCellMemoized
        key={id}
        colSpan={colSpan}
        rowSpan={isCollapse ? 2 : undefined}
        // При включённом ресайзе ширину колонки задаёт только <col> — см. раздел «Раскладка при
        // включённом ресайзе» в README. Гасит ширину сама ячейка, здесь достаточно флага.
        width={header.getSize()}
        isWidthFromColgroup={Boolean(columnResizing)}
        height={isWithGroups ? headHeight / 2 : headHeight}
        sorted={isSorted}
        cellHeight={cellHeight}
        canSort={canSort}
        tableVariant={variant}
        verticalAlign='middle'
      >
        {canSort ? (
          <StyledTableSortLabel
            direction={orderBy}
            IconComponent={StraightIcon}
            onClick={column.getToggleSortingHandler()}
            sorted={isSorted}
          >
            {flexRender(column.columnDef.header, header.getContext())}
          </StyledTableSortLabel>
        ) : (
          flexRender(header.column.columnDef.header, header.getContext())
        )}
        {columnResizing && !column.columns.length && (
          <ColumnResizer
            columnId={column.id}
            width={header.getSize()}
            onResize={columnResizing.handleResize}
            onReset={columnResizing.handleReset}
          />
        )}
      </TableCellMemoized>
    );
  };

  const mapCheckbox = () => {
    if (!enableRowSelection) {
      return null;
    }
    const isSecondRow = index === 1;
    if (isSecondRow) {
      return null;
    }
    return (
      <TableCellMemoized
        width={checkboxCellWidth}
        rowSpan={2}
        cellHeight={cellHeight}
        tableVariant={variant}
        sx={{
          padding: theme.spacing(1, 0.5),
        }}
      >
        <StyledCheckbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          disabled={isAllSelectionDisabled}
          onChange={table.getToggleAllRowsSelectedHandler()}
          checkboxOffsetX={checkboxOffsetX}
          sx={{
            transform: `translateX(${checkboxOffsetX}px) translateY(${checkboxOffsetY}px)`,
          }}
        />
      </TableCellMemoized>
    );
  };

  return (
    <StyledTableHeadRow variant={variant}>
      {isWithSubRows && (
        <TableCellMemoized width={arrowCellWidth} cellHeight={cellHeight} tableVariant={variant} />
      )}
      {mapCheckbox()}
      {headerGroup.headers.map(mapHeader)}
    </StyledTableHeadRow>
  );
};

export {TableHeadRow};
